import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { FormsModule, NgForm, NgModel } from "@angular/forms";
import { UserService } from "../../../features/user/user.service";
import { AuthService } from '../auth.service';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil, timeout } from "rxjs/operators";
import { CommonModule } from "@angular/common";
import { Register } from "src/app/shared/model/register";
import { Gender, genderDisplay } from "src/app/shared/model/gender";
import { EMPTY, Subject } from "rxjs";
import { LoaderComponent } from "../../../shared/components/loader/loader.component";
import { HttpErrorResponse } from "@angular/common/http";



@Component({
  imports: [CommonModule, FormsModule, LoaderComponent],
  selector: "app-register",
  templateUrl: "./register.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./register.component.css"],
})
export class RegisterComponent implements OnInit, OnDestroy {
  // component state trackers
  public showLoader: boolean = false;

  // tracking error msg
  public showInputErrorMessages: boolean = false;
  public showEmailErrorMessages: boolean = false;
  public showMarketingErrorMessage = false;
  public showPwdNotTheSameMessage: boolean = false;
  public emailAlreadyExists: boolean = false;
  public fetchedIncompleteUserData: boolean = false;
  public emailValidationTimeout: any;
  public showEmailFormatError: boolean = false;
  public registerError!: {
    hasErrored: boolean;
    message: string;
  };
  public passwordValidation = {
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecialChar: false,
    hasMinLength: false,
  };

  // form interaction
  public showPassword = false;
  public isExpanded = false;

  // Subjects
  public inputSsnSubject: Subject<string> = new Subject();
  public inputEmailSubject: Subject<string> = new Subject();
  private destroy$ = new Subject<void>();

  // DOM binds
  @Output() loadingStateChanged: EventEmitter<boolean> = new EventEmitter();
  @Output() userRegistered: EventEmitter<any> = new EventEmitter();

  // form data
  public genderDisplay = genderDisplay;
  public Gender = Gender;
  public registerData!: Register;
  public passwordRepeated = "";

  @ViewChild("registerForm", { static: false }) registerForm!: NgForm;
  @ViewChild("socialSecurityNumberControl", { static: false })
  socialSecurityNumberControl!: NgModel;

  public errorMessages = {
    ssn: {
      wrongFormat: "Personnummeret måste vara 12 tecken (ååååmmdd-xxxx)",
      required: "Personnummer är obligatoriskt",
    },
    names: {
      first: {
        required: "Förnamn är obligatoriskt",
      },
      last: {
        required: "Efternamn är obligatoriskt",
      },
    },
    city: {
      required: "Stad är obligatoriskt",
    },
    street: {
      required: "Gata är obligatoriskt",
    },
    zipCode: {
      required: "Postnummer är obligatoriskt",
      length: "Postnumret måste vara 5 siffror långt",
      allZeros: "Postnumret kan inte vara alla nollor",
    },
    gender: {
      defaultOption: "Inget kön valt",
    },
    email: {
      wrongFormat: "Ogiltig e-postadress",
      required: "Email är obligatoriskt",
      emailInUse: "E-mail addressen används redan. Har du redan ett konto?",
    },
    phone: {
      required: "Telefonnummer är obligatoriskt",
      length: "Telefonnummret måste vara 10 siffror långt",
      allZeros: "Telefonnumret kan inte vara alla nollor",
    },
    pwd: {
      upperCase: "Minst en stor bokstav",
      lowerCase: "Minst en liten bokstav",
      number: "Minst en siffra",
      specialChar: "Minst ett specialtecken",
      minLength: "Minst 6 karaktärer",
      required: "Lösenordsfältet får inte vara tom",
      notMatching: "Lösenorden är inte lika",
    },
    registerError: {
      existingUser: "Användaren finns redan. Har du redan ett konto?",
      emailInUse: "E-mail addressen används redan. Har du redan ett konto?",
      ssnInUse: "Personnummret används redan. Har du redan ett konto?",
    },
    fetchedIncompleteUserData:
      "Kunde inte hämta hitta all nödvändig information.",
  };

  constructor(
    private userService: UserService,
    private AuthService: AuthService,
    private cdr: ChangeDetectorRef,
  ) {}
  public dataFetchedFromSsn: boolean = false;
  public ngOnInit() {
    this.registerData = new Register();
    this.registerError = {
      hasErrored: false,
      message: "",
    };

    this.inputSsnSubject
      .pipe(
        debounceTime(500),
        distinctUntilChanged(),
        switchMap((ssn) => {
          this.fetchedIncompleteUserData = false;
          this.showLoader = true;

          return this.userService.getUserData(ssn).pipe(
            timeout(25000),
            catchError((error) => {
              this.fetchedIncompleteUserData = true;
              this.toggleSection(true);
              return EMPTY;
            }),
            finalize(() => (this.showLoader = false)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (userData) => {
          if (!userData) {
            this.fetchedIncompleteUserData = true;
            this.toggleSection(true);
            return;
          }

          this.populateFormFromSsnFetch(userData);
          this.cdr.markForCheck();
        },
      });

    this.inputEmailSubject
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        switchMap((email) => {
          const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;

          if (!email || !emailRegex.test(email)) {
            this.emailAlreadyExists = false;
            return EMPTY;
          }

          this.showLoader = true;

          return this.AuthService.ValidateEmail(email).pipe(
            catchError(() => {
              this.emailAlreadyExists = false;
              return EMPTY;
            }),

            finalize(() => (this.showLoader = false)),
          );
        }),
        takeUntil(this.destroy$),
      )
      .subscribe({
        next: (exists: boolean) => {
          this.emailAlreadyExists = exists;
        },
      });
  }

  public ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete(); // Complete this subject
  }

  public toggleSection(value: boolean) {
    this.isExpanded = value;
  }

  public togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  public setShowErrorMessages() {
    if (!this.registerData.agreeMarketing) {
      this.showMarketingErrorMessage = true;
    }

    this.showInputErrorMessages = true;
    this.showPwdNotTheSameMessage = !this.doPasswordsMatch();
  }

  public isPasswordValid(): boolean {
    return (
      this.passwordValidation.hasUppercase &&
      this.passwordValidation.hasLowercase &&
      this.passwordValidation.hasNumber &&
      this.passwordValidation.hasSpecialChar &&
      this.passwordValidation.hasMinLength
    );
  }

  public doPasswordsMatch(): boolean {
    return this.registerData.password === this.passwordRepeated;
  }

  public phoneFetchedFromSsn: boolean = false;
  private populateFormFromSsnFetch(userData: any) {
    this.dataFetchedFromSsn = true;
    userData.firstName
      ? (this.registerData.firstName = userData.firstName)
      : (this.fetchedIncompleteUserData = true);

    userData.lastName
      ? (this.registerData.lastName = userData.lastName)
      : (this.fetchedIncompleteUserData = true);

    if (Object.values(Gender).includes(userData.gender)) {
      this.registerData.gender = userData.gender as Gender;
    } else {
      this.registerData.gender = Gender.EjValt;
      this.fetchedIncompleteUserData = true;
    }

    if (userData.phoneNumber) {
      this.registerData.phoneNumber = userData.phoneNumber;
      this.phoneFetchedFromSsn = true;
    } else {
      this.phoneFetchedFromSsn = false;
    }

    // Populate address data
    if (userData.address) {
      userData.address.city
        ? (this.registerData.address.city = userData.address.city)
        : (this.fetchedIncompleteUserData = true);

      userData.address.street
        ? (this.registerData.address.street = userData.address.street)
        : (this.fetchedIncompleteUserData = true);

      if (userData.address.zipCode) {
        const normalizedZipCode = userData.address.zipCode.replace(/\s+/g, "");
        this.registerData.address.zipCode = normalizedZipCode;
        if (!userData.address.country) {
          this.userService.getLocationByZipCode(normalizedZipCode).subscribe({
            next: (data) => {
              if (data?.country) {
                this.registerData.address.country = data.country;
                this.cdr.detectChanges();
              }
            },
            error: () => {},
          });
        } else {
          this.registerData.address.country = userData.address.country;
        }
      } else {
        this.fetchedIncompleteUserData = true;
      }
    } else {
      this.fetchedIncompleteUserData = true;
    }

    this.toggleSection(true);
  }

  public registerNewUser(): void {
    this.registerData.socialSecurityNumber =
      this.registerData.socialSecurityNumber || null;

    this.userService.addUser(this.registerData).subscribe({
      next: (res) => {
        this.userRegistered.emit({
          password: this.registerData.password,
          email: this.registerData.email,
        });
      },
      error: (error: HttpErrorResponse) => {
        this.registerError = {
          hasErrored: true,
          message: "Ett fel uppstod vid registreringen",
        };

        const resErrors: { message: string }[] = error?.error?.errors || [];

        if (resErrors.length === 0) return;

        const resErrorMessages = resErrors.map((err) => err.message);

        if (
          resErrorMessages.includes("Användaren är redan registrerad (e-mail)")
        ) {
          this.registerError.message =
            this.errorMessages.registerError.emailInUse;
        }

        if (resErrorMessages.includes("Användaren är redan registrerad (ssn)"))
          this.registerError.message =
            this.errorMessages.registerError.ssnInUse;
      },
    });
  }

  public onInputChangeEmail(event: any): void {
    const email = event.target.value;
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$/;
    this.inputEmailSubject.next(email);

    if (!email) {
      this.emailAlreadyExists = false;
      this.showEmailErrorMessages = false;
      return;
    }
    this.showEmailFormatError = !emailRegex.test(email);
  }
  public comparePwds(passwordRepeated: string) {
    this.passwordRepeated = passwordRepeated;
    this.updatePasswordMatchStatus();
  }

  public updatePasswordMatchStatus(): void {
    this.showPwdNotTheSameMessage =
      !!this.passwordRepeated &&
      (this.registerData.password || "") !== this.passwordRepeated;
  }
  public onInputChangeZipCode(event: any): void {
    event.target.value = event.target.value.replace(/\D/g, "");
    const zip = event.target.value;
    this.registerData.address.zipCode = zip;

    this.registerData.address.city = "";
    this.registerData.address.country = "";

    if (zip.length === 5) {
      this.userService.getLocationByZipCode(zip).subscribe({
        next: (data: any[] | null) => {
          if (!data?.length) return;
          const parts = data[0].display_name
            .split(",")
            .map((p: string) => p.trim());
          const city = (parts[2] ?? parts[1] ?? "").replace(/\s*kommun$/i, "");
          this.registerData.address.city = city;
          this.registerData.address.country = "Sverige";
          this.cdr.detectChanges();
        },
        error: () => {},
      });
    }
  }
  private extractDataFromSsn(ssn: string): void {
    const digits = ssn.replace(/\D/g, "");

    if (digits.length !== 12) return;

    const year = digits.substring(0, 4);
    const month = digits.substring(4, 6);
    const day = digits.substring(6, 8);
    this.registerData.birthDate = `${year}-${month}-${day}`;
    const genderDigit = parseInt(digits[10]);
    this.registerData.gender =
      genderDigit % 2 !== 0 ? Gender.Man : Gender.Kvinna;
  }
  public onInputChangeSSN(event: any): void {
    const numericInput = event.target.value.replace(/\D/g, "");
    if (numericInput.length === 0) {
      this.dataFetchedFromSsn = false;
      this.phoneFetchedFromSsn = false;
      this.registerData.socialSecurityNumber = "";
      return;
    }
    const formatedInput =
      numericInput.length > 8
        ? `${numericInput.slice(0, 8)}-${numericInput.slice(8)}`
        : numericInput;
    event.target.value = formatedInput;
    this.registerData.socialSecurityNumber = formatedInput;

    if (numericInput.length === 12) {
      this.extractDataFromSsn(numericInput);
      this.inputSsnSubject.next(numericInput);
    }
  }
  get phoneHasLengthError(): boolean {
    return (
      !!this.registerData.phoneNumber &&
      this.registerData.phoneNumber.length !== 10
    );
  }
  public get phoneIsAllZeros(): boolean {
    return (
      !!this.registerData.phoneNumber &&
      /^0{10}$/.test(this.registerData.phoneNumber)
    );
  }

  public get zipIsAllZeros(): boolean {
    return (
      !!this.registerData.address.zipCode &&
      /^0{5}$/.test(this.registerData.address.zipCode)
    );
  }
  public onInputChangeNummericOnly(event: any): void {
    event.target.value = event.target.value.replace(/\D/g, "");
    this.registerData.phoneNumber = event.target.value;
  }

  public onGenderChange(value: string): void {
    this.registerData.gender = +value; // Converts string to number
  }

  public onPasswordChange(): void {
    const password = this.registerData.password || "";

    this.passwordValidation = {
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasNumber: /\d/.test(password),
      hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
      hasMinLength: password.length >= 6,
    };
    this.updatePasswordMatchStatus();
  }

  public onAgreeMarketingChanged(value: boolean) {
    this.registerData.agreeMarketing = value;
  }

  public onSubscribeToEmailChanged(value: boolean) {
    this.registerData.subscribeToEmailNotification = value;
  }
}

